import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Star, 
  StarOff, 
  Save, 
  X, 
  MapPin, 
  Briefcase,
  DollarSign,
  Download,
  Loader2
} from 'lucide-react';
import { API } from '../App';
import { toast } from 'sonner';

const JobsAdminPage = ({ user, token }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importSettings, setImportSettings] = useState({
    location: '',
    max_jobs: 30
  });
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    location: '',
    salary: '',
    contract_type: 'full-time',
    sector: '',
    spanish_level: 'intermediate',
    description: '',
    requirements: '',
    url: '',
    is_featured: false
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/admin/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const jobData = {
      ...formData,
      requirements: formData.requirements.split('\n').filter(r => r.trim())
    };

    try {
      const url = editingJob 
        ? `${API}/jobs/${editingJob.id}`
        : `${API}/jobs`;
      
      const method = editingJob ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(jobData)
      });

      if (response.ok) {
        await fetchJobs();
        resetForm();
      }
    } catch (error) {
      console.error('Error saving job:', error);
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      company: job.company,
      location: job.location,
      salary: job.salary || '',
      contract_type: job.contract_type,
      sector: job.sector,
      spanish_level: job.spanish_level,
      description: job.description,
      requirements: job.requirements.join('\n'),
      url: job.url,
      is_featured: job.is_featured || false
    });
    setShowForm(true);
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta oferta?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchJobs();
      }
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  const toggleFeatured = async (jobId) => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}/toggle-featured`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchJobs();
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const handleImportFromAdzuna = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${API}/admin/import-adzuna-jobs?location=${importSettings.location}&max_jobs=${importSettings.max_jobs}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`✅ Importación completada: ${result.imported} nuevos, ${result.updated} actualizados`);
        await fetchJobs();
        setShowImportModal(false);
        setImportSettings({ location: '', max_jobs: 30 });
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail || 'Error al importar trabajos'}`);
      }
    } catch (error) {
      console.error('Error importing jobs:', error);
      toast.error('Error al conectar con Adzuna');
    } finally {
      setImporting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      company: '',
      location: '',
      salary: '',
      contract_type: 'full-time',
      sector: '',
      spanish_level: 'intermediate',
      description: '',
      requirements: '',
      url: '',
      is_featured: false
    });
    setEditingJob(null);
    setShowForm(false);
  };

  const getSpanishLevelBadge = (level) => {
    const variants = {
      native: { color: 'bg-emerald-700 text-white', label: 'Nativo' },
      advanced: { color: 'bg-emerald-600 text-white', label: 'Avanzado' },
      intermediate: { color: 'bg-teal-600 text-white', label: 'Intermedio' },
      basic: { color: 'bg-teal-500 text-white', label: 'Básico' },
      'not-required': { color: 'bg-slate-200 text-slate-700', label: 'No requerido' }
    };
    const variant = variants[level] || variants['intermediate'];
    return <Badge className={`${variant.color}`}>{variant.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Gestión de Ofertas</h1>
            <p className="text-slate-600 mt-2">Administra las ofertas de trabajo de JobSuiza</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowImportModal(true)}
              variant="outline"
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            >
              <Download className="w-5 h-5 mr-2" />
              Importar de Adzuna
            </Button>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-blue-900 hover:bg-blue-800"
            >
              {showForm ? <X className="w-5 h-5 mr-2" /> : <Plus className="w-5 h-5 mr-2" />}
              {showForm ? 'Cancelar' : 'Nueva Oferta'}
            </Button>
          </div>
        </div>

        {/* Adzuna Import Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Importar Trabajos desde Adzuna</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="import-location">Ubicación (opcional)</Label>
                  <Input
                    id="import-location"
                    value={importSettings.location}
                    onChange={(e) => setImportSettings({...importSettings, location: e.target.value})}
                    placeholder="Ej: Zurich, Geneva, Basel (vacío = toda Suiza)"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Deja vacío para buscar en toda Suiza
                  </p>
                </div>

                <div>
                  <Label htmlFor="import-max">Máximo de trabajos</Label>
                  <Input
                    id="import-max"
                    type="number"
                    min="1"
                    max="100"
                    value={importSettings.max_jobs}
                    onChange={(e) => setImportSettings({...importSettings, max_jobs: parseInt(e.target.value)})}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Recomendado: 20-50 trabajos por importación
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Nota:</strong> La importación buscará trabajos que requieran español y usará IA para detectar el nivel requerido. Esto puede tomar algunos minutos.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleImportFromAdzuna}
                    disabled={importing}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Importar
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowImportModal(false);
                      setImportSettings({ location: '', max_jobs: 30 });
                    }}
                    variant="outline"
                    disabled={importing}
                  >
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{editingJob ? 'Editar Oferta' : 'Nueva Oferta de Trabajo'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="title">Título del Puesto *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      placeholder="Ej: Desarrollador Full Stack"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company">Empresa *</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      required
                      placeholder="Ej: Tech Company SA"
                    />
                  </div>

                  <div>
                    <Label htmlFor="location">Ubicación *</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      required
                      placeholder="Ej: Zürich"
                    />
                  </div>

                  <div>
                    <Label htmlFor="salary">Salario</Label>
                    <Input
                      id="salary"
                      value={formData.salary}
                      onChange={(e) => setFormData({...formData, salary: e.target.value})}
                      placeholder="Ej: CHF 80,000 - 100,000"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contract_type">Tipo de Contrato *</Label>
                    <select
                      id="contract_type"
                      value={formData.contract_type}
                      onChange={(e) => setFormData({...formData, contract_type: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md"
                      required
                    >
                      <option value="full-time">Tiempo Completo</option>
                      <option value="part-time">Tiempo Parcial</option>
                      <option value="contract">Contrato</option>
                      <option value="freelance">Freelance</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="sector">Sector *</Label>
                    <Input
                      id="sector"
                      value={formData.sector}
                      onChange={(e) => setFormData({...formData, sector: e.target.value})}
                      required
                      placeholder="Ej: Tecnología"
                    />
                  </div>

                  <div>
                    <Label htmlFor="spanish_level">Nivel de Español *</Label>
                    <select
                      id="spanish_level"
                      value={formData.spanish_level}
                      onChange={(e) => setFormData({...formData, spanish_level: e.target.value})}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md"
                      required
                    >
                      <option value="native">Nativo</option>
                      <option value="advanced">Avanzado</option>
                      <option value="intermediate">Intermedio</option>
                      <option value="basic">Básico</option>
                      <option value="not-required">No Requerido</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="url">URL de la Oferta *</Label>
                    <Input
                      id="url"
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({...formData, url: e.target.value})}
                      required
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Descripción *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                    rows={4}
                    placeholder="Describe el puesto de trabajo..."
                  />
                </div>

                <div>
                  <Label htmlFor="requirements">Requisitos (uno por línea) *</Label>
                  <Textarea
                    id="requirements"
                    value={formData.requirements}
                    onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                    required
                    rows={4}
                    placeholder="Ej:&#10;React y Node.js&#10;3+ años de experiencia&#10;Español fluido"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_featured"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_featured" className="cursor-pointer">
                    Marcar como destacada (aparecerá en el carrusel de la homepage)
                  </Label>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" className="bg-blue-900 hover:bg-blue-800">
                    <Save className="w-4 h-4 mr-2" />
                    {editingJob ? 'Actualizar' : 'Crear'} Oferta
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Jobs List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-slate-900">
              Todas las Ofertas ({jobs.length})
            </h2>
          </div>

          {jobs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-600">No hay ofertas de trabajo aún.</p>
                <Button
                  onClick={() => setShowForm(true)}
                  className="mt-4 bg-blue-900 hover:bg-blue-800"
                >
                  Crear Primera Oferta
                </Button>
              </CardContent>
            </Card>
          ) : (
            jobs.map((job) => (
              <Card key={job.id} className={`${!job.is_active ? 'opacity-50' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        {job.is_featured && (
                          <Badge className="bg-yellow-500 text-white">
                            <Star className="w-3 h-3 mr-1" />
                            Destacada
                          </Badge>
                        )}
                        {getSpanishLevelBadge(job.spanish_level)}
                        {!job.is_active && (
                          <Badge variant="outline" className="text-red-600 border-red-600">
                            Inactiva
                          </Badge>
                        )}
                      </div>

                      <h3 className="text-2xl font-bold text-slate-900 mb-2">{job.title}</h3>
                      <p className="text-lg font-semibold text-blue-900 mb-3">{job.company}</p>

                      <div className="flex flex-wrap gap-4 mb-3 text-sm">
                        <div className="flex items-center text-slate-600">
                          <MapPin className="w-4 h-4 mr-1" />
                          {job.location}
                        </div>
                        {job.salary && (
                          <div className="flex items-center text-slate-600">
                            <DollarSign className="w-4 h-4 mr-1" />
                            {job.salary}
                          </div>
                        )}
                        <div className="flex items-center text-slate-600">
                          <Briefcase className="w-4 h-4 mr-1" />
                          {job.contract_type}
                        </div>
                      </div>

                      <p className="text-slate-600 line-clamp-2 mb-2">{job.description}</p>
                      <p className="text-xs text-slate-500">
                        Publicado: {new Date(job.created_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => toggleFeatured(job.id)}
                        title={job.is_featured ? 'Quitar de destacadas' : 'Marcar como destacada'}
                      >
                        {job.is_featured ? (
                          <StarOff className="w-4 h-4" />
                        ) : (
                          <Star className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(job)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(job.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default JobsAdminPage;
